<template>
  <!--
  ===================================================================================================================================================
  🔷 BANNER / VISOR DE VERSIÓN Y ACTUALIZACIONES (IONIC) — SofyApps
     ✅ iPhone / PWA robusto:
        1) Overlay HTML/CSS INSTANTÁNEO durante instalación (fiable aunque el reload sea inmediato).
        2) Confirmación "Actualización aplicada ✅" tras reload (sessionStorage *just-updated*),
           mostrada tras nextTick() para asegurar render.
     ✅ Compatible con 2 PWAs (normal + /kiosk): flags namespaced desde window.__pwa.flags
  ===================================================================================================================================================
  -->

  <!-- ✅ OVERLAY INSTANTÁNEO (no depende de Ionic) -->
  <div
    v-if="installOverlayVisible"
    class="pwa-install-overlay"
    role="status"
    aria-live="polite"
  >
    <div class="pwa-install-overlay__card">
      <div class="pwa-install-overlay__spinner" />
      <div class="pwa-install-overlay__title">Instalando actualización…</div>
      <div class="pwa-install-overlay__subtitle">
        Esto puede tardar unos segundos
      </div>
    </div>
  </div>

  <!-- =========================================================================================
       🔹 ICONO FLOTANTE
      ========================================================================================= -->
  <IonChip
    v-if="fabVisible"
    class="pwa-update-fab bg-transparent"
    @click="sheetOpen = true"
  >
    <IonIcon :icon="informationCircleOutline" class="text-theme" />
  </IonChip>

  <!-- =========================================================================================
       🔹 ACTION SHEET (Información y acciones)
      ========================================================================================= -->
  <IonActionSheet
    :is-open="sheetOpen"
    header="Información de versión y actualizaciones"
    :buttons="sheetButtons"
    @didDismiss="sheetOpen = false"
  />

  <!-- =========================================================================================
       🔹 LOADING (solo para "buscar", NO para instalar)
      ========================================================================================= -->
  <IonLoading
    :is-open="loading"
    message="Buscando actualizaciones..."
    spinner="crescent"
    @didDismiss="loading = false"
  />

  <!-- =========================================================================================
       🔹 ALERTA DE RESULTADO
      ========================================================================================= -->
  <IonAlert
    :is-open="alertOpen"
    :header="alertHeader"
    :message="alertMessage"
    :buttons="alertButtons"
    @didDismiss="alertOpen = false"
  />
</template>

<script setup lang="ts">
// ⛳ Imports
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import {
  IonChip,
  IonIcon,
  IonActionSheet,
  IonLoading,
  IonAlert,
} from "@ionic/vue";
import { informationCircleOutline, closeOutline } from "ionicons/icons";

// ⛳ Props
const props = defineProps<{ fabVisible?: boolean }>();
const fabVisible = computed(() => props.fabVisible ?? true);

// versión inyectada por Vite (define)
const semver = __SEMVER__;
const build = __APP_VERSION__;

// UI State
const sheetOpen = ref(false);

// 📁 Estado banner
const loading = ref(false);
const hasUpdate = ref(false);

// ✅ Overlay instantáneo (instalación)
const installOverlayVisible = ref(false);

// Alert
const alertOpen = ref(false);
const alertHeader = ref("");
const alertMessage = ref("");
const alertButtons = ref<any[]>([{ text: "Cerrar", role: "cancel" }]);

// ---------------------------------------------------------------------------------------------
// Session helpers (iOS-safe)
// ---------------------------------------------------------------------------------------------
function ssGet(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function ssSet(key: string, val: string | null) {
  try {
    if (val === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, val);
  } catch {
    // ignore
  }
}

// ✅ Legacy keys (siempre disponibles)
const LEGACY_UPDATING = "pwa:updating";
const LEGACY_JUST_UPDATED = "pwa:just-updated";
const LEGACY_BLOCK_AUTO = "pwa:block-auto";

// ✅ NUEVO (recovery iOS): recordamos última build vista (por PWA normal / kiosk separado)
const LAST_BUILD_KEY = "pwa:last-build";

// ---------------------------------------------------------------------------------------------
// ✅ Flags: compatibles con 2 PWAs (normal + kiosk)
// ---------------------------------------------------------------------------------------------
const FLAG_UPDATING = ref(LEGACY_UPDATING);
const FLAG_JUST_UPDATED = ref(LEGACY_JUST_UPDATED);
const FLAG_BLOCK_AUTO = ref(LEGACY_BLOCK_AUTO);

function resolveFlagsOnce() {
  const pwa = (window as any).__pwa;

  const upd = pwa?.flags?.updating;
  const just = pwa?.flags?.justUpdated;
  const block = pwa?.flags?.blockAuto;

  if (typeof upd === "string" && upd.length) FLAG_UPDATING.value = upd;
  if (typeof just === "string" && just.length) FLAG_JUST_UPDATED.value = just;
  if (typeof block === "string" && block.length) FLAG_BLOCK_AUTO.value = block;
}

/**
 * Intento robusto: si __pwa aún no existe, reintenta unos ticks.
 */
async function resolveFlags() {
  resolveFlagsOnce();

  const alreadyResolved =
    FLAG_UPDATING.value !== LEGACY_UPDATING ||
    FLAG_JUST_UPDATED.value !== LEGACY_JUST_UPDATED ||
    FLAG_BLOCK_AUTO.value !== LEGACY_BLOCK_AUTO;

  if (alreadyResolved) return;
  if ((window as any).__pwa) return;

  for (let i = 0; i < 8; i++) {
    await new Promise((r) => setTimeout(r, 150));
    resolveFlagsOnce();
    if ((window as any).__pwa) break;
  }
}

// ✅ helpers: leer/escribir “en ambos mundos”
function readFlag(namespacedKey: string, legacyKey: string) {
  return ssGet(namespacedKey) ?? ssGet(legacyKey);
}
function setFlagBoth(
  namespacedKey: string,
  legacyKey: string,
  val: string | null,
) {
  ssSet(namespacedKey, val);
  ssSet(legacyKey, val);
}

// ✅ ¿usuario rechazó auto-update?
function isBlocked() {
  const b = readFlag(FLAG_BLOCK_AUTO.value, LEGACY_BLOCK_AUTO);
  return b === "1";
}
function clearBlocked() {
  setFlagBoth(FLAG_BLOCK_AUTO.value, LEGACY_BLOCK_AUTO, null);
}

// ✅ NUEVO: recovery iOS cuando controllerchange no llegó
function recoverIfStuckUpdating() {
  // si el usuario bloqueó, no tocamos nada
  if (isBlocked()) return;

  const upd = readFlag(FLAG_UPDATING.value, LEGACY_UPDATING);
  if (upd !== "1") {
    // siempre guardamos la build actual como “última vista”
    try {
      localStorage.setItem(LAST_BUILD_KEY, build);
    } catch {}
    return;
  }

  // Si estaba "updating=1" pero la build ha cambiado respecto a la última vista,
  // significa que ya estamos en la nueva versión => damos por aplicada.
  try {
    const prev = localStorage.getItem(LAST_BUILD_KEY);
    if (prev && prev !== build) {
      // limpia updating y dispara just-updated
      setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, null);
      setFlagBoth(FLAG_JUST_UPDATED.value, LEGACY_JUST_UPDATED, "1");
    }
    localStorage.setItem(LAST_BUILD_KEY, build);
  } catch {
    // si localStorage falla, al menos no dejamos overlay infinito
    setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, null);
  }
}

// ---------------------------------------------------------------------------------------------
// Handlers de eventos globales (main.ts)
// ---------------------------------------------------------------------------------------------
function onNeedRefresh() {
  hasUpdate.value = true;
}
function onOpenSheet() {
  sheetOpen.value = true;
}

// ---------------------------------------------------------------------------------------------
// ✅ Confirmación post-update (fiable)
//    ✅ FIX: apaga overlay SIEMPRE cuando se detecta aplicada
// ---------------------------------------------------------------------------------------------
async function showJustUpdatedIfAny() {
  if (isBlocked()) {
    setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, null);
    installOverlayVisible.value = false;
    return;
  }

  const just = readFlag(FLAG_JUST_UPDATED.value, LEGACY_JUST_UPDATED);

  if (just === "1") {
    // limpia flags
    setFlagBoth(FLAG_JUST_UPDATED.value, LEGACY_JUST_UPDATED, null);
    setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, null);

    // ✅ CLAVE: si no, iOS puede dejar el overlay “pegado”
    installOverlayVisible.value = false;

    await nextTick();
    setTimeout(() => {
      alertHeader.value = "Actualización aplicada ✅";
      alertMessage.value = `Ahora estás usando la última versión (v${__SEMVER__}).`;
      alertButtons.value = [{ text: "Cerrar", role: "cancel" }];
      alertOpen.value = true;
    }, 0);
  }
}

// ---------------------------------------------------------------------------------------------
// ✅ Instalación con overlay instantáneo
// ---------------------------------------------------------------------------------------------
async function instalarAhora() {
  sheetOpen.value = false;
  alertOpen.value = false;

  clearBlocked();

  installOverlayVisible.value = true;

  setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, "1");
  setFlagBoth(FLAG_JUST_UPDATED.value, LEGACY_JUST_UPDATED, null);

  await nextTick();
  requestAnimationFrame(() => {
    const apply = (window as any).__pwa?.applyUpdateNow;
    if (!apply) {
      alertHeader.value = "La actualización aún se está preparando";
      alertMessage.value =
        "La nueva versión ya se ha detectado, pero todavía no está lista para instalar. Inténtalo de nuevo en unos segundos.";
      alertButtons.value = [{ text: "Cerrar", role: "cancel" }];
      alertOpen.value = true;
      installOverlayVisible.value = false;
      return;
    }
    apply();
  });
}

// ---------------------------------------------------------------------------------------------
// 🔎 buscarActualizaciones()
// ---------------------------------------------------------------------------------------------
async function buscarActualizaciones() {
  loading.value = true;
  hasUpdate.value = false;

  let remote: { version?: string; build?: string } | null = null;

  try {
    try {
      const res = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: "no-store",
      });
      if (res.ok) remote = await res.json();
    } catch {}

    await (window as any).__pwa?.checkNow?.();

    await new Promise((r) => setTimeout(r, 600));

    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.waiting) hasUpdate.value = true;
      else if (reg?.installing) {
        await new Promise<void>((resolve) => {
          const installing = reg.installing!;
          const handler = () => {
            if (
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              hasUpdate.value = true;
              installing.removeEventListener("statechange", handler);
              resolve();
            }
          };
          installing.addEventListener("statechange", handler);
          setTimeout(() => resolve(), 1200);
        });
      }
    } catch {}

    const localSemver = __SEMVER__;
    const remoteSemver = remote?.version;
    const hayNuevaSegunServidor = !!(
      remoteSemver && remoteSemver !== localSemver
    );

    if (hasUpdate.value) {
      alertHeader.value = "Se ha encontrado una nueva versión";
      alertMessage.value = remoteSemver
        ? `Estás en v${localSemver} — disponible v${remoteSemver}.`
        : "Hay una actualización lista para instalar.";
      alertButtons.value = [
        { text: "Actualizar ahora", handler: () => instalarAhora() },
        { text: "Cerrar", role: "cancel", icon: closeOutline },
      ];
    } else if (hayNuevaSegunServidor) {
      alertHeader.value = "Nueva versión detectada";
      alertMessage.value = `Se ha detectado la versión v${remoteSemver}, pero todavía no está preparada para instalar. Cierra y vuelve a abrir la app en unos segundos, o vuelve a buscar actualizaciones.`;
      alertButtons.value = [
        { text: "Cerrar", role: "cancel", icon: closeOutline },
      ];
    } else {
      alertHeader.value = "No se han encontrado nuevas actualizaciones";
      alertMessage.value = `Estás usando la última versión (v${localSemver}).`;
      alertButtons.value = [
        { text: "Cerrar", role: "cancel", icon: closeOutline },
      ];
    }
  } finally {
    loading.value = false;
    sheetOpen.value = false;
    alertOpen.value = true;
  }
}

// ---------------------------------------------------------------------------------------------
// Action sheet buttons (computed)
// ---------------------------------------------------------------------------------------------
const sheetButtons = computed(() => [
  { text: `ℹ️  Versión: ${semver}`, handler: () => {} },
  { text: `📄  Build: ${build}`, handler: () => {} },
  {
    text: "🔄  Buscar actualizaciones",
    handler: () => buscarActualizaciones(),
  },

  ...(hasUpdate.value
    ? [{ text: "⬇️  Instalar actualización", handler: () => instalarAhora() }]
    : []),

  { text: "❌ Cerrar", role: "cancel" },
]);

function onPwaReady() {
  resolveFlagsOnce();

  if (isBlocked()) {
    installOverlayVisible.value = false;
    return;
  }

  const upd = readFlag(FLAG_UPDATING.value, LEGACY_UPDATING);
  installOverlayVisible.value = upd === "1";
}

// ---------------------------------------------------------------------------------------------
// Ciclo de vida
// ---------------------------------------------------------------------------------------------
onMounted(async () => {
  window.addEventListener("pwa:need-refresh", onNeedRefresh);
  window.addEventListener("pwa:open-sheet", onOpenSheet);
  window.addEventListener("pwa:ready", onPwaReady);

  await resolveFlags();

  // ✅ FIX iOS: si se quedó “instalando” pero ya estamos en build nueva, lo recuperamos
  recoverIfStuckUpdating();

  if (isBlocked()) {
    installOverlayVisible.value = false;
    setFlagBoth(FLAG_UPDATING.value, LEGACY_UPDATING, null);
    return;
  }

  await showJustUpdatedIfAny();

  const upd = readFlag(FLAG_UPDATING.value, LEGACY_UPDATING);
  installOverlayVisible.value = upd === "1";
});

onBeforeUnmount(() => {
  window.removeEventListener("pwa:need-refresh", onNeedRefresh);
  window.removeEventListener("pwa:open-sheet", onOpenSheet);
  window.removeEventListener("pwa:ready", onPwaReady);
});
</script>

<style>
/* Botón flotante */
.pwa-update-fab {
  position: fixed;
  right: max(12px, env(safe-area-inset-right));
  bottom: max(12px, env(safe-area-inset-bottom));
  right: max(12px, constant(safe-area-inset-right));
  bottom: max(12px, constant(safe-area-inset-bottom));
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2147483647;
  will-change: transform;
}
.pwa-update-fab:active {
  transform: translateY(1px) scale(0.98);
}

/* ✅ Overlay instantáneo (iOS-proof) */
.pwa-install-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
}
.pwa-install-overlay__card {
  width: min(340px, 92vw);
  background: #fff;
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  text-align: center;
}
.pwa-install-overlay__spinner {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  border: 3px solid rgba(0, 0, 0, 0.18);
  border-top-color: rgba(0, 0, 0, 0.55);
  margin: 4px auto 10px;
  animation: pwaSpin 0.8s linear infinite;
}
.pwa-install-overlay__title {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 2px;
}
.pwa-install-overlay__subtitle {
  font-size: 13px;
  opacity: 0.75;
}
@keyframes pwaSpin {
  to {
    transform: rotate(360deg);
  }
}
</style>
